<template>
  <section class="mx-auto flex min-h-[70vh] max-w-6xl flex-col gap-8 px-6 py-10">
    <div class="overflow-hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.14),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(251,191,36,0.16),_transparent_28%),linear-gradient(160deg,_rgba(14,18,28,0.98),_rgba(17,24,39,0.94))] p-6 shadow-glow sm:p-8">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-3xl">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetsPage.eyebrow') }}</p>
          <h1 class="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {{ $t('meetsPage.title') }}
          </h1>
          <p class="mt-3 text-base text-steel-200/90">
            {{ $t('meetsPage.description') }}
          </p>
        </div>
        <button
          type="button"
          class="rounded-2xl bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
          @click="openCreateModal"
        >
          {{ $t('meetsPage.hostEvent') }}
        </button>
      </div>
    </div>

    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <div
        v-for="item in eventHighlights"
        :key="item.key"
        class="rounded-2xl border border-white/10 bg-midnight-800/60 p-4 shadow-glow"
      >
        <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ item.label }}</p>
        <p class="mt-3 text-3xl font-semibold text-white">{{ item.value }}</p>
        <p class="mt-2 text-sm text-steel-300">{{ item.caption }}</p>
      </div>
    </div>

    <div class="rounded-2xl border border-white/10 bg-midnight-800/60 p-4 shadow-glow">
      <div class="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
        <label class="text-sm text-steel-200">
          {{ $t('meetsPage.city') }}
          <input
            v-model.trim="filters.city"
            type="text"
            :placeholder="$t('meetsPage.searchCity')"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          />
        </label>
        <label class="text-sm text-steel-200">
          {{ $t('meetsPage.eventType') }}
          <select
            v-model="filters.type"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          >
            <option value="">{{ $t('meetsPage.allTypes') }}</option>
            <option v-for="option in eventTypeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <div class="flex items-end">
          <button
            type="button"
            class="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
            @click="resetFilters"
          >
            {{ $t('meetsPage.reset') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="grid gap-4 lg:grid-cols-2">
      <div
        v-for="item in 4"
        :key="`meet-skeleton-${item}`"
        class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-800/60 animate-pulse"
      >
        <div class="aspect-[16/8] bg-white/10"></div>
        <div class="space-y-3 p-5">
          <div class="h-3 w-28 rounded bg-white/10"></div>
          <div class="h-6 w-2/3 rounded bg-white/10"></div>
          <div class="h-3 w-full rounded bg-white/10"></div>
          <div class="h-3 w-3/4 rounded bg-white/10"></div>
        </div>
      </div>
    </div>

    <div v-else-if="!filteredEvents.length" class="rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-12 text-center">
      <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetsPage.noEventsEyebrow') }}</p>
      <p class="mt-3 text-sm text-steel-300">{{ $t('meetsPage.noEventsDescription') }}</p>
    </div>

    <div v-else class="grid gap-4 lg:grid-cols-2">
      <article
        v-for="event in filteredEvents"
        :key="event.id"
        class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-800/60 shadow-glow"
      >
        <button
          type="button"
          class="block aspect-[16/8] w-full overflow-hidden bg-midnight-900/70"
          @click="openImageLightbox(event)"
        >
          <img
            v-if="event.coverImage"
            :src="event.coverImage"
            :alt="event.title"
            class="h-full w-full object-cover"
            :style="{ objectPosition: event.coverPosition }"
          />
          <div v-else class="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.18),_transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(0,0,0,0.12))] text-xs uppercase tracking-[0.35em] text-steel-400">
            {{ $t('meetsPage.eventFallback') }}
          </div>
        </button>

        <div class="space-y-4 p-5">
          <div class="flex flex-wrap items-center gap-2">
            <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">
              {{ event.eventTypeLabel }}
            </span>
            <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">
              {{ event.statusLabel }}
            </span>
            <span v-if="event.isHost" class="rounded-full border border-neon-400/20 bg-neon-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-neon-300">
              {{ $t('meetsPage.youHost') }}
            </span>
          </div>

          <div>
            <h2 class="text-2xl font-semibold text-white">{{ event.title }}</h2>
            <p class="mt-2 text-sm leading-6 text-steel-300">{{ event.description || $t('meetsPage.noDescription') }}</p>
          </div>

          <div class="grid gap-3 sm:grid-cols-2">
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ $t('meetsPage.location') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ event.locationLabel }}</p>
              <p class="mt-1 text-sm text-steel-300">{{ event.venue || $t('meetsPage.venueTbd') }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ $t('meetsPage.schedule') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ event.startsAtLabel }}</p>
              <p class="mt-1 text-sm text-steel-300">{{ event.endsAtLabel }}</p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-3">
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-steel-300">
              <span class="font-semibold text-white">{{ event.attendeeCountLabel }}</span> {{ $t('meetsPage.going') }}
            </div>
            <div v-if="event.capacityLabel" class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-steel-300">
              {{ $t('meetsPage.capacity') }} <span class="font-semibold text-white">{{ event.capacityLabel }}</span>
            </div>
          </div>

          <div class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/50 px-4 py-3">
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="event.hostAvatar"
                :src="event.hostAvatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">{{ event.hostInitials }}</div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <router-link :to="`/u/${event.hostHandle}`" class="truncate text-sm font-semibold text-white hover:text-neon-400">
                  {{ event.hostName }}
                </router-link>
                <verified-badge v-if="event.hostVerified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.24em] text-steel-400">@{{ event.hostHandle }}</p>
            </div>
          </div>

          <div class="flex flex-wrap gap-3">
            <router-link
              :to="`/meets/${event.id}`"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
            >
              {{ $t('meetsPage.viewEvent') }}
            </router-link>
            <a
              :href="event.mapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
            >
              {{ $t('meetsPage.openMaps') }}
            </a>
            <button
              v-if="!event.isHost && !event.isGoing"
              type="button"
              class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="busyMeetId === event.id"
              @click="joinMeet(event)"
            >
              {{ busyMeetId === event.id ? $t('meetsPage.joining') : $t('meetsPage.rsvpGoing') }}
            </button>
            <button
              v-else-if="!event.isHost && event.isGoing"
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="busyMeetId === event.id"
              @click="leaveMeet(event)"
            >
              {{ busyMeetId === event.id ? $t('meetsPage.updating') : $t('meetsPage.cancelRsvp') }}
            </button>
            <button
              v-if="event.isHost && event.status !== 'cancelled'"
              type="button"
              class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="busyMeetId === event.id"
              @click="cancelMeet(event)"
            >
              {{ busyMeetId === event.id ? $t('meetsPage.updating') : $t('meetsPage.cancelEvent') }}
            </button>
            <button
              v-if="event.isHost"
              type="button"
              class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="busyMeetId === event.id"
              @click="confirmDeleteMeet(event)"
            >
              {{ busyMeetId === event.id ? $t('meetsPage.updating') : $t('meetsPage.deleteEvent') }}
            </button>
          </div>
        </div>
      </article>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>

  <div
      v-if="showCreateModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeCreateModal"
    >
      <div class="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetsPage.hostEventModalEyebrow') }}</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">{{ $t('meetsPage.hostEventModalTitle') }}</h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeCreateModal"
          >
            {{ $t('meetsPage.close') }}
          </button>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200 sm:col-span-2">
            {{ $t('meetsPage.titleLabel') }}
            <input
              v-model.trim="meetForm.title"
              type="text"
              maxlength="90"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Sunday morning cars & coffee"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Event type
            <select
              v-model="meetForm.event_type"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            >
              <option v-for="option in eventTypeOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="block text-sm text-steel-200">
            Max attendees
            <input
              v-model.number="meetForm.max_attendees"
              type="number"
              min="1"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="50"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Country
            <input
              v-model.trim="meetForm.country"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Romania"
            />
          </label>
          <label class="block text-sm text-steel-200">
            City
            <input
              v-model.trim="meetForm.city"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Bucharest"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Coordinates or Google Maps link
            <div class="mt-2 flex flex-col gap-3 sm:flex-row">
              <input
                v-model.trim="meetForm.location_query"
                type="text"
                class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                placeholder="44.4268, 26.1025 or paste a Google Maps link"
              />
              <button
                type="button"
                class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="creating"
                @click="resolveMeetLocation"
              >
                Use location
              </button>
            </div>
            <p class="mt-2 text-xs text-steel-400">DRVN will use the detected coordinates for maps and can autofill nearby place details.</p>
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Venue
            <input
              v-model.trim="meetForm.venue"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Promenada rooftop parking"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Starts at
            <input
              v-model="meetForm.starts_at"
              type="datetime-local"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Ends at
            <input
              v-model="meetForm.ends_at"
              type="datetime-local"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Description
            <textarea
              v-model.trim="meetForm.description"
              rows="5"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Who it is for, how the route works, where people should park, and any rules."
            ></textarea>
          </label>
          <div class="sm:col-span-2 rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-white">Event cover</p>
                <p class="mt-1 text-xs text-steel-400">Optional photo stored in the existing `posts` bucket. Drag inside the preview to position it.</p>
              </div>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
                <input class="hidden" type="file" accept="image/*" @change="handleMeetImageUpload" />
                Upload photo
              </label>
            </div>
            <div
              class="mt-4 aspect-[16/8] overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70"
              :class="{ 'cursor-grab active:cursor-grabbing': meetForm.coverImage }"
              @pointerdown="startMeetPreviewDrag"
            >
              <img
                v-if="meetForm.coverImage"
                :src="meetForm.coverImage"
                alt="Meet cover preview"
                class="h-full w-full object-cover"
                :style="{ objectPosition: meetPreviewPosition }"
                draggable="false"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No image uploaded
              </div>
            </div>
            <div v-if="meetForm.coverImage" class="pointer-events-none mt-3 text-center text-[10px] uppercase tracking-[0.3em] text-white/70">
              Drag to reframe the event cover
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-end gap-3">
          <p v-if="formStatusMessage" class="mr-auto text-sm text-steel-300">{{ formStatusMessage }}</p>
          <p v-if="formErrorMessage" class="mr-auto text-sm text-rose-200">{{ formErrorMessage }}</p>
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeCreateModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="creating"
            @click="createMeet"
          >
            {{ creating ? 'Saving...' : 'Create event' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeDeleteModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete this event?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will permanently remove the event and all RSVPs attached to it.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeDeleteModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="busyMeetId === meetToDelete?.id"
            @click="deleteMeet"
          >
            {{ busyMeetId === meetToDelete?.id ? 'Deleting...' : 'Delete event' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="lightboxEvent"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 px-6"
      @click.self="closeImageLightbox"
    >
      <div class="relative w-full max-w-5xl">
        <button
          type="button"
          class="absolute right-0 top-[-3rem] rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
          @click="closeImageLightbox"
        >
          Close
        </button>
        <div class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/80">
          <img
            v-if="lightboxEvent.coverImage"
            :src="lightboxEvent.coverImage"
            :alt="lightboxEvent.title"
            class="max-h-[80vh] w-full object-contain"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { emitAppToast } from '@/lib/toast'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

const defaultMeetForm = () => ({
  title: '',
  description: '',
  event_type: 'meet',
  location_query: '',
  country: '',
  city: '',
  venue: '',
  latitude: null,
  longitude: null,
  starts_at: '',
  ends_at: '',
  cover_image_url: '',
  cover_image_path: '',
  cover_offset_x: 50,
  cover_offset_y: 50,
  coverImage: '',
  max_attendees: null,
})

export default {
  name: 'LocalMeetsView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      userId: '',
      events: [],
      attendeeIdsByMeet: {},
      loading: true,
      errorMessage: '',
      filters: {
        city: '',
        type: '',
      },
      showCreateModal: false,
      meetForm: defaultMeetForm(),
      meetPreviewDrag: null,
      creating: false,
      busyMeetId: '',
      showDeleteModal: false,
      meetToDelete: null,
      lightboxEvent: null,
      formErrorMessage: '',
      formStatusMessage: '',
      eventTypeOptions: [
        { value: 'meet', label: 'Meet' },
        { value: 'drive', label: 'Drive' },
        { value: 'track', label: 'Track Day' },
        { value: 'cars_and_coffee', label: 'Cars & Coffee' },
      ],
    }
  },
  computed: {
    filteredEvents() {
      const city = String(this.filters.city || '').trim().toLowerCase()
      const type = String(this.filters.type || '').trim()

      return this.events.filter((event) => {
        const matchesCity = !city
          || String(event.city || '').toLowerCase().includes(city)
          || String(event.country || '').toLowerCase().includes(city)
          || String(event.venue || '').toLowerCase().includes(city)
        const matchesType = !type || event.event_type === type
        return matchesCity && matchesType
      })
    },
    eventHighlights() {
      const upcoming = this.events.filter((event) => event.status === 'scheduled').length
      const going = this.events.filter((event) => event.isGoing).length
      const hosting = this.events.filter((event) => event.isHost).length
      const cities = new Set(this.events.map((event) => `${event.country}-${event.city}`).filter(Boolean)).size

      return [
        { key: 'upcoming', label: 'Upcoming', value: upcoming, caption: 'Open events on the calendar.' },
        { key: 'going', label: 'Your RSVPs', value: going, caption: 'Meets you already joined.' },
        { key: 'hosting', label: 'Hosting', value: hosting, caption: 'Events created from your account.' },
        { key: 'cities', label: 'Cities', value: cities, caption: 'Local scenes currently active.' },
      ]
    },
    meetPreviewPosition() {
      return `${this.meetForm.cover_offset_x}% ${this.meetForm.cover_offset_y}%`
    },
  },
  async mounted() {
    await this.refreshView()
  },
  methods: {
    resetFilters() {
      this.filters.city = ''
      this.filters.type = ''
    },
    eventTypeLabel(value) {
      const map = {
        meet: 'Meet',
        drive: 'Drive',
        track: 'Track Day',
        cars_and_coffee: 'Cars & Coffee',
      }
      return map[value] || 'Meet'
    },
    eventStatusLabel(value) {
      const map = {
        scheduled: 'Scheduled',
        cancelled: 'Cancelled',
        completed: 'Completed',
      }
      return map[value] || 'Scheduled'
    },
    formatDateTime(value) {
      if (!value) return 'Date TBD'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'Date TBD'
      return parsed.toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    buildMeetImageUrl({ cover_image_path, cover_image_url }) {
      if (cover_image_path) {
        return getPostImageUrl({ imagePath: cover_image_path, imageUrl: cover_image_url })
      }
      return cover_image_url || ''
    },
    buildMapsUrl(event) {
      if (Number.isFinite(event.latitude) && Number.isFinite(event.longitude)) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.latitude},${event.longitude}`)}`
      }
      const query = [event.venue, event.city, event.country].filter(Boolean).join(', ')
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || event.title || 'DRVN event')}`
    },
    normalizeEvent(event) {
      const attendeeIds = this.attendeeIdsByMeet[event.id] || []
      const hostName = event.host?.full_name || 'DRVN Member'
      const attendeeCount = attendeeIds.length
      return {
        ...event,
        coverImage: this.buildMeetImageUrl(event),
        coverPosition: `${Number.isFinite(event.cover_offset_x) ? event.cover_offset_x : 50}% ${Number.isFinite(event.cover_offset_y) ? event.cover_offset_y : 50}%`,
        mapsUrl: this.buildMapsUrl(event),
        eventTypeLabel: this.eventTypeLabel(event.event_type),
        statusLabel: this.eventStatusLabel(event.status),
        startsAtLabel: this.formatDateTime(event.starts_at),
        endsAtLabel: event.ends_at ? `Ends ${this.formatDateTime(event.ends_at)}` : 'No set end time',
        locationLabel: [event.city, event.country].filter(Boolean).join(', ') || 'Location TBD',
        hostName,
        hostHandle: event.host?.handle || 'member',
        hostAvatar: getAvatarUrl({ avatarPath: event.host?.avatar_path, avatarUrl: event.host?.avatar_url }),
        hostVerified: Boolean(event.host?.verified),
        hostInitials: hostName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'D',
        attendeeCount,
        attendeeCountLabel: attendeeCount.toLocaleString(),
        capacityLabel: Number.isFinite(event.max_attendees) ? Number(event.max_attendees).toLocaleString() : '',
        isGoing: attendeeIds.includes(this.userId),
        isHost: event.host_user_id === this.userId,
      }
    },
    async refreshView() {
      this.loading = true
      this.errorMessage = ''

      try {
        const session = await refreshReadySession()
        this.userId = session?.user?.id || ''

        const eventsResult = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('local_meets')
            .select(`
              id, host_user_id, title, description, event_type, country, city, venue, starts_at, ends_at,
              cover_image_url, cover_image_path, cover_offset_x, cover_offset_y, latitude, longitude, max_attendees, status, created_at,
              host:profiles!local_meets_host_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)
            `)
            .order('starts_at', { ascending: true })
        ))

        if (eventsResult.error) {
          this.errorMessage = 'Unable to load local events right now.'
          this.events = []
          return
        }

        const rawEvents = eventsResult.data || []
        const meetIds = rawEvents.map((event) => event.id)
        let attendeeMap = {}

        if (meetIds.length) {
          const attendeesResult = await runSupabaseQueryWithRetry(() => (
            supabase
              .from('local_meet_attendees')
              .select('meet_id, user_id')
              .in('meet_id', meetIds)
          ))

          if (!attendeesResult.error) {
            attendeeMap = (attendeesResult.data || []).reduce((accumulator, row) => {
              if (!accumulator[row.meet_id]) accumulator[row.meet_id] = []
              accumulator[row.meet_id].push(row.user_id)
              return accumulator
            }, {})
          }
        }

        this.attendeeIdsByMeet = attendeeMap
        this.events = rawEvents.map((event) => this.normalizeEvent(event))
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      this.formErrorMessage = ''
      this.formStatusMessage = ''
      this.meetForm = defaultMeetForm()
      this.showCreateModal = true
    },
    closeCreateModal() {
      this.showCreateModal = false
      this.meetPreviewDrag = null
      this.creating = false
      this.formErrorMessage = ''
      this.formStatusMessage = ''
      this.meetForm = defaultMeetForm()
    },
    confirmDeleteMeet(event) {
      this.meetToDelete = event
      this.showDeleteModal = true
      this.errorMessage = ''
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.meetToDelete = null
    },
    openImageLightbox(event) {
      if (!event?.coverImage) return
      this.lightboxEvent = event
    },
    closeImageLightbox() {
      this.lightboxEvent = null
    },
    startMeetPreviewDrag(event) {
      if (!this.meetForm.coverImage) return
      event.preventDefault()
      const target = event.currentTarget
      if (!target) return
      if (typeof target.setPointerCapture === 'function') {
        try {
          target.setPointerCapture(event.pointerId)
        } catch (_error) {
          // ignore if capture fails
        }
      }

      const rect = target.getBoundingClientRect()
      this.meetPreviewDrag = {
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: this.meetForm.cover_offset_x,
        startOffsetY: this.meetForm.cover_offset_y,
        width: rect.width || 1,
        height: rect.height || 1,
      }

      const onMove = (moveEvent) => {
        if (!this.meetPreviewDrag) return
        moveEvent.preventDefault()
        const dx = moveEvent.clientX - this.meetPreviewDrag.startX
        const dy = moveEvent.clientY - this.meetPreviewDrag.startY
        const nextX = this.meetPreviewDrag.startOffsetX + (dx / this.meetPreviewDrag.width) * 100
        const nextY = this.meetPreviewDrag.startOffsetY + (dy / this.meetPreviewDrag.height) * 100
        this.meetForm.cover_offset_x = Math.max(0, Math.min(100, Math.round(nextX)))
        this.meetForm.cover_offset_y = Math.max(0, Math.min(100, Math.round(nextY)))
      }

      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        this.meetPreviewDrag = null
      }

      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', onUp)
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.74, maxPixels = 1400000 }) {
      const imageObjectUrl = URL.createObjectURL(file)
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = imageObjectUrl
      })

      try {
        const originalWidth = image.width || 1
        const originalHeight = image.height || 1
        const widthScale = Math.min(1, maxWidth / originalWidth)
        const pixelScale = Math.min(1, Math.sqrt(maxPixels / (originalWidth * originalHeight || 1)))
        const scale = Math.min(widthScale, pixelScale)
        const targetWidth = Math.max(1, Math.round(originalWidth * scale))
        const targetHeight = Math.max(1, Math.round(originalHeight * scale))

        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas is not available.')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
        if (!blob) throw new Error('Image conversion failed.')
        return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
      } finally {
        URL.revokeObjectURL(imageObjectUrl)
      }
    },
    async handleMeetImageUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.formErrorMessage = ''
      this.formStatusMessage = ''

      const session = await refreshReadySession()
      const userId = session?.user?.id
      if (!userId) {
        this.formErrorMessage = 'Please sign in again to upload an event image.'
        return
      }

      let uploadFile = file
      try {
        uploadFile = await this.convertImageToWebp(file, { maxWidth: 1600 })
      } catch (_error) {
        this.formErrorMessage = 'Photo processing failed. Try a different image.'
        return
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `local-meets/${userId}/${Date.now()}-${safeName}`

      const { error } = await supabase.storage
        .from('posts')
        .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })

      if (error) {
        this.formErrorMessage = 'Upload failed. Make sure the "posts" bucket exists.'
        return
      }

      const coverImage = getPostImageUrl({ imagePath: filePath })
      this.meetForm.cover_image_path = filePath
      this.meetForm.cover_image_url = coverImage
      this.meetForm.cover_offset_x = 50
      this.meetForm.cover_offset_y = 50
      this.meetForm.coverImage = coverImage
      this.formStatusMessage = 'Event cover uploaded.'
      if (event?.target) {
        event.target.value = ''
      }
    },
    async createMeet() {
      this.formErrorMessage = ''
      this.formStatusMessage = ''

      const session = await refreshReadySession()
      const userId = session?.user?.id
      if (!userId) {
        this.formErrorMessage = 'Please sign in again to create an event.'
        return
      }

      const title = String(this.meetForm.title || '').trim()
      const country = String(this.meetForm.country || '').trim()
      const city = String(this.meetForm.city || '').trim()
      const startsAtIso = this.normalizeDateTimeInput(this.meetForm.starts_at)
      const endsAtIso = this.normalizeDateTimeInput(this.meetForm.ends_at)

      const missingFields = []
      if (!title) missingFields.push('title')
      if (!country) missingFields.push('country')
      if (!city) missingFields.push('city')
      if (!startsAtIso) missingFields.push('start time')

      if (missingFields.length) {
        this.formErrorMessage = `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
        return
      }

      if (endsAtIso && new Date(endsAtIso).getTime() < new Date(startsAtIso).getTime()) {
        this.formErrorMessage = 'End time must be after the start time.'
        return
      }

      this.creating = true
      const payload = {
        host_user_id: userId,
        title: title.slice(0, 90),
        description: this.meetForm.description,
        event_type: this.meetForm.event_type,
        country,
        city,
        venue: this.meetForm.venue,
        latitude: Number.isFinite(this.meetForm.latitude) ? this.meetForm.latitude : null,
        longitude: Number.isFinite(this.meetForm.longitude) ? this.meetForm.longitude : null,
        starts_at: startsAtIso,
        ends_at: endsAtIso,
        cover_image_url: this.meetForm.cover_image_url,
        cover_image_path: this.meetForm.cover_image_path,
        cover_offset_x: this.meetForm.cover_offset_x,
        cover_offset_y: this.meetForm.cover_offset_y,
        max_attendees: Number.isFinite(this.meetForm.max_attendees) ? this.meetForm.max_attendees : null,
        status: 'scheduled',
      }

      const { error } = await supabase.from('local_meets').insert(payload)
      this.creating = false

      if (error) {
        this.formErrorMessage = error.message || 'Unable to create this event.'
        return
      }

      emitAppToast({ title: 'Event created', message: 'Your local meet is now live.', tone: 'success' })
      await this.refreshView()
      this.closeCreateModal()
    },
    parseCoordinates(value) {
      const input = String(value || '').trim()
      if (!input) return null

      const simpleMatch = input.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/)
      if (simpleMatch) {
        const latitude = Number(simpleMatch[1])
        const longitude = Number(simpleMatch[2])
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          return { latitude, longitude }
        }
      }

      const atMatch = input.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/)
      if (atMatch) {
        return { latitude: Number(atMatch[1]), longitude: Number(atMatch[2]) }
      }

      const queryMatch = input.match(/[?&]q=(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/)
      if (queryMatch) {
        return { latitude: Number(queryMatch[1]), longitude: Number(queryMatch[2]) }
      }

      return null
    },
    async reverseGeocode(latitude, longitude) {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) {
        throw new Error('Location lookup failed.')
      }
      return response.json()
    },
    async resolveMeetLocation() {
      this.formErrorMessage = ''
      this.formStatusMessage = ''

      const parsed = this.parseCoordinates(this.meetForm.location_query)
      if (!parsed) {
        this.formErrorMessage = 'Enter coordinates like "44.4268, 26.1025" or paste a Google Maps link with coordinates.'
        return
      }

      this.meetForm.latitude = parsed.latitude
      this.meetForm.longitude = parsed.longitude

      try {
        const data = await this.reverseGeocode(parsed.latitude, parsed.longitude)
        const address = data?.address || {}
        this.meetForm.country = this.meetForm.country || address.country || ''
        this.meetForm.city = this.meetForm.city || address.city || address.town || address.village || address.county || ''
        this.meetForm.venue = this.meetForm.venue || data?.name || address.road || data?.display_name || ''
        this.formStatusMessage = 'Coordinates applied to the event location.'
      } catch (_error) {
        this.formStatusMessage = 'Coordinates saved. Fill the location text manually if needed.'
      }
    },
    async joinMeet(event) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in again to RSVP.'
        return
      }

      this.busyMeetId = event.id
      const { error } = await supabase.from('local_meet_attendees').insert({
        meet_id: event.id,
        user_id: this.userId,
        status: 'going',
      })
      this.busyMeetId = ''

      if (error) {
        this.errorMessage = error.message || 'Unable to join this event.'
        return
      }

      emitAppToast({ title: 'RSVP confirmed', message: 'You are on the list for this event.', tone: 'success' })
      await this.refreshView()
    },
    async leaveMeet(event) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in again to update your RSVP.'
        return
      }

      this.busyMeetId = event.id
      const { error } = await supabase
        .from('local_meet_attendees')
        .delete()
        .eq('meet_id', event.id)
        .eq('user_id', this.userId)
      this.busyMeetId = ''

      if (error) {
        this.errorMessage = error.message || 'Unable to cancel your RSVP.'
        return
      }

      emitAppToast({ title: 'RSVP removed', message: 'You are no longer marked as going.', tone: 'info' })
      await this.refreshView()
    },
    async cancelMeet(event) {
      this.busyMeetId = event.id
      const { error } = await supabase
        .from('local_meets')
        .update({ status: 'cancelled' })
        .eq('id', event.id)
        .eq('host_user_id', this.userId)
      this.busyMeetId = ''

      if (error) {
        this.errorMessage = error.message || 'Unable to cancel this event.'
        return
      }

      emitAppToast({ title: 'Event cancelled', message: 'The meet has been marked as cancelled.', tone: 'info' })
      await this.refreshView()
    },
    async deleteMeet() {
      if (!this.meetToDelete?.id) {
        this.closeDeleteModal()
        return
      }

      this.busyMeetId = this.meetToDelete.id
      const { error } = await supabase
        .from('local_meets')
        .delete()
        .eq('id', this.meetToDelete.id)
        .eq('host_user_id', this.userId)
      this.busyMeetId = ''

      if (error) {
        this.errorMessage = error.message || 'Unable to delete this event.'
        return
      }

      emitAppToast({ title: 'Event deleted', message: 'Your event has been removed.', tone: 'info' })
      this.closeDeleteModal()
      await this.refreshView()
    },
    normalizeDateTimeInput(value) {
      const trimmed = String(value || '').trim()
      if (!trimmed) return ''
      const parsed = new Date(trimmed)
      if (Number.isNaN(parsed.getTime())) return ''
      return parsed.toISOString()
    },
  },
}
</script>
